import { RowSelectionState } from '@tanstack/react-table';
import { dateToUTC } from '@toggle/helpers';
import { EntityNotResolved, EntityResolved, User } from '@toggle/toggle';
import { isToday } from 'date-fns';

import { ONBOARDING_STATE, useRemoteStorage } from '~/hooks/use-remote-storage';
import { FormattedResolvedEntityV3Data } from '~/stores/use-watchlist/entityResolver';
import { useWatchlist } from '~/stores/use-watchlist/useWatchlist';
import { create } from '~/utils/store/createStore';

import { shouldOnBoardingBeActive } from '../utils/utils';

const prodReleaseStringDate = '2025-07-23T00:00:00Z';
const SUGGESTED_WATCHLIST_ID = 'suggestedWatchlistId';
const SUGGESTED_WATCHLIST_CREATION_DATE = 'suggestedWatchlistCreationDate';

type OnboardingStatus = 'completed' | 'not-started';
type OnboardingStep =
  | 'create-watchlist'
  | 'name-watchlist'
  | 'suggested-instruments'
  | 'follow-themes'
  | 'follow-geographies';

type FollowThemesEntryStep = 'name-watchlist' | 'suggested-instruments';

type OnboardingStore = {
  status: OnboardingStatus;
  activeStep: OnboardingStep;
  followThemesEntryStep: FollowThemesEntryStep | null;
  drawerOpen: boolean;
  isNewUser: boolean;
  entitiesList: EntityResolved[];
  unMappedEntitiesList: EntityNotResolved[];
  allDuplicateTickers: string[];
  rowSelection: RowSelectionState;
  resolvedEntityData: FormattedResolvedEntityV3Data;
  initStore: (user?: User) => void;
  setDrawerOpen: (open: boolean) => void;
  setActiveStep: (step: OnboardingStep) => void;
  getShowOnboarding: () => boolean;
  setEntitiesList: (entitiesList: EntityResolved[]) => void;
  setUnMappedEntitiesList: (unMappedEntitiesList: EntityNotResolved[]) => void;
  setRowSelection: (rowSelection: RowSelectionState) => void;
  setStatus: (status: OnboardingStatus) => void;
  setResolvedEntityData: (data?: FormattedResolvedEntityV3Data) => void;
  setAllDuplicateTickers: (allDuplicateTickers: string[]) => void;
  clearStore: () => void;
  hasSuggestedWatchlistId: () => boolean;
  getIsOnboardingFinishedToday: () => boolean;
  saveSuggestedWatchlistId: (watchlistId: string) => void;
};

export const useOnboardingStore = create<OnboardingStore>((set, get) => ({
  initStore: user => {
    const { items } = useRemoteStorage.getState();

    if (!user) {
      return;
    }

    const onboardingStatus = items?.['ONBOARDING_STATE'] ?? 'not-started';

    const isNewUser =
      dateToUTC(new Date(user.created_at)) >=
      dateToUTC(new Date(prodReleaseStringDate));

    set({ status: onboardingStatus as OnboardingStatus, isNewUser });
  },
  resolvedEntityData: {
    tags: [],
    duplicates: [],
    entities: [],
  },
  // Combined state for CSV search duplicates and table duplicates
  allDuplicateTickers: [],
  status: 'not-started',
  activeStep: 'create-watchlist',
  followThemesEntryStep: null,
  entitiesList: [],
  unMappedEntitiesList: [],
  rowSelection: {},
  isNewUser: false,
  drawerOpen: false,
  getShowOnboarding: () => {
    const { isNewUser, status } = get();
    return shouldOnBoardingBeActive({
      isNewUser,
      isCompleted: status === 'completed',
    });
  },
  setDrawerOpen: open => set({ drawerOpen: open }),
  setActiveStep: step =>
    set(state => {
      if (
        step === 'follow-themes' &&
        (state.activeStep === 'name-watchlist' ||
          state.activeStep === 'suggested-instruments')
      ) {
        return { activeStep: step, followThemesEntryStep: state.activeStep };
      }

      return { activeStep: step };
    }),
  setEntitiesList: entitiesList => set({ entitiesList }),
  setUnMappedEntitiesList: unMappedEntitiesList =>
    set({ unMappedEntitiesList }),
  setRowSelection: rowSelection => set({ rowSelection }),
  setStatus: status => {
    const { storeItems } = useRemoteStorage.getState();

    storeItems({
      [ONBOARDING_STATE]: status,
    });
    set({ status });
  },
  setResolvedEntityData: data => {
    const resolvedEntityData = data ?? {
      tags: [],
      duplicates: [],
      entities: [],
    };
    set({ resolvedEntityData });
  },
  setAllDuplicateTickers: allDuplicateTickers => set({ allDuplicateTickers }),
  clearStore: () => {
    const { setResolvedEntityData } = get();
    setResolvedEntityData();

    set({
      entitiesList: [],
      unMappedEntitiesList: [],
      resolvedEntityData: {
        tags: [],
        duplicates: [],
        entities: [],
      },
    });
  },
  hasSuggestedWatchlistId: () => {
    const watchlists = useWatchlist.getState().lists;
    const watchlistId = localStorage.getItem(SUGGESTED_WATCHLIST_ID);

    if (watchlistId) {
      return watchlists.some(watchlist => watchlist.id === watchlistId);
    }

    return false;
  },
  getIsOnboardingFinishedToday: () => {
    const createdDate = localStorage.getItem(SUGGESTED_WATCHLIST_CREATION_DATE);
    return !!createdDate && isToday(new Date(createdDate));
  },
  saveSuggestedWatchlistId: watchlistId => {
    localStorage.setItem(SUGGESTED_WATCHLIST_ID, watchlistId);
    localStorage.setItem(
      SUGGESTED_WATCHLIST_CREATION_DATE,
      new Date().toISOString()
    );
  },
}));
